import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImg from '../Assets/Logos/ibas_logo.jpeg';
import { getGradeDetails } from './gradeUtils';

const formatDate = (d) => {
    if (!d) return 'N/A';
    try {
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return String(d);
        return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return String(d);
    }
};

const runAutoTable = (doc, options) => {
    if (typeof autoTable === 'function') {
        return autoTable(doc, options);
    }
    if (autoTable && typeof autoTable.default === 'function') {
        return autoTable.default(doc, options);
    }
    if (typeof doc.autoTable === 'function') {
        return doc.autoTable(options);
    }
    throw new Error('autoTable plugin is not available on jsPDF');
};

export const generateTranscriptPdf = ({ courseGroup, breakdown, userProfile }) => {
    return new Promise((resolve, reject) => {
        try {
            const jsPDFConstructor = typeof jsPDF === 'function' ? jsPDF : (jsPDF?.jsPDF || jsPDF?.default || window.jspdf?.jsPDF);
            const doc = new jsPDFConstructor({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const buildPdf = (imgElement) => {
                try {
                    const pageWidth = 210;
                    const margin = 14;
                    const rightX = pageWidth - margin; // 196
                    const isStudent = userProfile?.role === 'student';

                    const {
                        caList = [],
                        finalExamList = [],
                        caEligibility = {},
                        finalPerformance = {},
                        overallMark = null,
                        overallGrade = 'Pending',
                        overallClassification = 'Pending Evaluation',
                        isFullyCompleted = false,
                    } = breakdown || {};

                    const studentName = userProfile?.name || 'Student';
                    const studentIndex = userProfile?.indexNumber || `STU-${userProfile?.id || '0000'}`;
                    const studentEmail = userProfile?.email || 'N/A';
                    const courseName = courseGroup?.courseName || 'Course';
                    const courseCode = courseGroup?.courseCode || 'N/A';
                    const issueDate = new Date().toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                    });

                    // ==========================================
                    // 1. INSTITUTIONAL HEADER & BRANDING
                    // ==========================================
                    let currentY = 12;

                    if (imgElement) {
                        try {
                            doc.addImage(imgElement, 'JPEG', margin, currentY, 44, 20);
                        } catch (imgErr) {
                            console.warn("Could not add image logo to PDF:", imgErr);
                        }
                    }

                    doc.setFontSize(18);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(3, 36, 94); // #03245e
                    doc.text("OFFICIAL ACADEMIC TRANSCRIPT", rightX, currentY + 7, { align: 'right' });

                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(2, 132, 199); // #0284c7
                    doc.text("INSTITUTE OF BUSINESS AND ACCOUNTING STUDIES", rightX, currentY + 12, { align: 'right' });

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text(`Date of Issue: ${issueDate}`, rightX, currentY + 17, { align: 'right' });

                    currentY = 36;
                    doc.setFontSize(8.5);
                    doc.setTextColor(100, 116, 139);
                    doc.text("Accredited Institution for Higher Professional Education | Web: www.ibas.lk", margin, currentY);

                    // Divider
                    currentY += 4;
                    doc.setDrawColor(2, 132, 199);
                    doc.setLineWidth(0.8);
                    doc.line(margin, currentY, rightX, currentY);

                    // ==========================================
                    // 2. STUDENT & COURSE PROFILE
                    // ==========================================
                    currentY += 5;
                    const infoBoxStartY = currentY;
                    const halfWidth = (rightX - margin - 6) / 2;

                    // Student Information Box
                    doc.setFillColor(248, 250, 252);
                    doc.setDrawColor(226, 232, 240);
                    doc.roundedRect(margin, infoBoxStartY, halfWidth, 28, 2, 2, 'FD');

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(2, 132, 199);
                    doc.text("STUDENT CREDENTIALS", margin + 4, infoBoxStartY + 6);

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Full Name:", margin + 4, infoBoxStartY + 12);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    const safeStudentName = String(studentName).length > 28 ? String(studentName).substring(0, 26) + '...' : String(studentName);
                    doc.text(safeStudentName, margin + 28, infoBoxStartY + 12, { maxWidth: halfWidth - 30 });

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Student ID:", margin + 4, infoBoxStartY + 18);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    doc.text(String(studentIndex), margin + 28, infoBoxStartY + 18, { maxWidth: halfWidth - 30 });

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Email:", margin + 4, infoBoxStartY + 24);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    const safeEmail = String(studentEmail).length > 30 ? String(studentEmail).substring(0, 28) + '...' : String(studentEmail);
                    doc.text(safeEmail, margin + 28, infoBoxStartY + 24, { maxWidth: halfWidth - 30 });

                    // Course Information Box
                    const rightColX = margin + halfWidth + 6;
                    doc.setFillColor(248, 250, 252);
                    doc.setDrawColor(226, 232, 240);
                    doc.roundedRect(rightColX, infoBoxStartY, halfWidth, 28, 2, 2, 'FD');

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(2, 132, 199);
                    doc.text("PROGRAMME & MODULE DETAILS", rightColX + 4, infoBoxStartY + 6);

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Course:", rightColX + 4, infoBoxStartY + 12);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    const safeCourseName = String(courseName).length > 28 ? String(courseName).substring(0, 26) + '...' : String(courseName);
                    doc.text(safeCourseName, rightColX + 28, infoBoxStartY + 12, { maxWidth: halfWidth - 30 });

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Module Code:", rightColX + 4, infoBoxStartY + 18);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    doc.text(String(courseCode), rightColX + 28, infoBoxStartY + 18, { maxWidth: halfWidth - 30 });

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(71, 85, 105);
                    doc.text("Scheme:", rightColX + 4, infoBoxStartY + 24);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(15, 23, 42);
                    doc.text("CA (50%) + Final Exam (50%)", rightColX + 28, infoBoxStartY + 24, { maxWidth: halfWidth - 30 });

                    currentY = infoBoxStartY + 33;

                    // ==========================================
                    // 3. PART 1: CONTINUOUS ASSESSMENTS (CA)
                    // ==========================================
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(3, 36, 94);
                    doc.text("PART 1: CONTINUOUS ASSESSMENTS (CA) — 50% WEIGHTAGE", margin, currentY);

                    currentY += 4.5;
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text("Eligibility Criteria for Final Exam: Continuous Assessment Average >= 50.0%", margin, currentY);

                    currentY += 3;

                    const caTableHeaders = [["#", "Assessment Component", "Due Date", "Status", "Marks (%)", "Grade Awarded"]];

                    const caTableRows = caList.map((item, idx) => {
                        const m = item.attempt?.marks;
                        const hasMarks = m !== null && m !== undefined && m !== '' && !isNaN(Number(m));
                        const status = !item.attempt || !item.attempt.id
                            ? 'Not Attempted'
                            : (hasMarks ? 'Graded' : 'Submitted');
                        const gradeText = hasMarks
                            ? `Grade ${getGradeDetails(m).grade} (${getGradeDetails(m).classification})`
                            : 'Pending';

                        return [
                            String(idx + 1),
                            String(item.name || item.title || `Continuous Assessment ${idx + 1}`),
                            formatDate(item.endDate),
                            status,
                            hasMarks ? `${Number(m)}%` : '-',
                            gradeText
                        ];
                    });

                    if (caTableRows.length === 0) {
                        caTableRows.push(["-", "No Continuous Assessments assigned.", "-", "-", "-", "-"]);
                    }

                    runAutoTable(doc, {
                        startY: currentY,
                        head: caTableHeaders,
                        body: caTableRows,
                        theme: 'grid',
                        headStyles: {
                            fillColor: [3, 36, 94],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold',
                            fontSize: 8.5,
                            cellPadding: 3,
                        },
                        columnStyles: {
                            0: { cellWidth: 10, halign: 'center' },
                            1: { cellWidth: 76 },
                            2: { cellWidth: 30 },
                            3: { cellWidth: 24, halign: 'center' },
                            4: { cellWidth: 20, halign: 'right' },
                            5: { cellWidth: 22, halign: 'center' },
                        },
                        styles: { fontSize: 8, cellPadding: 2.5 },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                const statusColIdx = 3;
                                if (data.column.index === statusColIdx) {
                                    const text = String(data.cell.raw || '');
                                    if (text === 'Graded') {
                                        data.cell.styles.textColor = [22, 163, 74];
                                        data.cell.styles.fontStyle = 'bold';
                                    } else if (text === 'Submitted') {
                                        data.cell.styles.textColor = [217, 119, 6];
                                    } else {
                                        data.cell.styles.textColor = [220, 38, 38];
                                    }
                                }
                            }
                        }
                    });

                    currentY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : currentY + 30) + 3;

                    // CA Eligibility Status Strip
                    const bannerHeight = 8;
                    let bannerBg = [240, 253, 244]; // green
                    let bannerBorder = [187, 247, 208];
                    let bannerTextColor = [22, 101, 52];

                    if (caEligibility.status === 'NOT_ELIGIBLE') {
                        bannerBg = [254, 242, 242]; // red
                        bannerBorder = [254, 202, 202];
                        bannerTextColor = [153, 27, 27];
                    } else if (caEligibility.status === 'PENDING') {
                        bannerBg = [255, 251, 235]; // amber
                        bannerBorder = [254, 243, 199];
                        bannerTextColor = [146, 64, 14];
                    }

                    doc.setFillColor(bannerBg[0], bannerBg[1], bannerBg[2]);
                    doc.setDrawColor(bannerBorder[0], bannerBorder[1], bannerBorder[2]);
                    doc.roundedRect(margin, currentY, rightX - margin, bannerHeight, 1.5, 1.5, 'FD');

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(bannerTextColor[0], bannerTextColor[1], bannerTextColor[2]);
                    const caAvgStr = caEligibility.caAverage !== null && caEligibility.caAverage !== undefined ? `${caEligibility.caAverage}%` : 'N/A';
                    const bannerLeft = `Continuous Assessment Average: ${caAvgStr} (Cutoff: >= 50%)`;
                    const bannerRight = `Status: ${caEligibility.label || 'Pending'}`;
                    doc.text(bannerLeft, margin + 4, currentY + 5.5);
                    doc.text(bannerRight, rightX - 4, currentY + 5.5, { align: 'right' });

                    currentY += bannerHeight + 6;

                    // ==========================================
                    // 4. PART 2: FINAL EXAMINATION
                    // ==========================================
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(3, 36, 94);
                    doc.text("PART 2: FINAL EXAMINATION — 50% WEIGHTAGE", margin, currentY);

                    currentY += 4.5;
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text("Evaluated Component for Final Composite Award", margin, currentY);

                    currentY += 3;

                    const finalTableHeaders = isStudent
                        ? [["#", "Examination Component", "Due Date", "Status", "Grade Awarded"]]
                        : [["#", "Examination Component", "Due Date", "Status", "Marks (%)", "Grade Awarded"]];

                    const finalTableRows = finalExamList.map((item, idx) => {
                        if (caEligibility.status === 'NOT_ELIGIBLE') {
                            return isStudent
                                ? [String(idx + 1), String(item.name || item.title || 'Final Examination'), formatDate(item.endDate), 'Locked (Ineligible)', 'Grade F (Fail)']
                                : [String(idx + 1), String(item.name || item.title || 'Final Examination'), formatDate(item.endDate), 'Locked (Ineligible)', '0%', 'Grade F (Fail)'];
                        }

                        const m = item.attempt?.marks;
                        const hasMarks = m !== null && m !== undefined && m !== '' && !isNaN(Number(m));
                        const status = !item.attempt || !item.attempt.id
                            ? 'Not Attempted'
                            : (hasMarks ? 'Graded' : 'Submitted');
                        const gradeText = hasMarks
                            ? `Grade ${getGradeDetails(m).grade} (${getGradeDetails(m).classification})`
                            : 'Pending';

                        if (isStudent) {
                            // STRICTLY NO MARKS FOR STUDENT!
                            return [
                                String(idx + 1),
                                String(item.name || item.title || 'Final Examination'),
                                formatDate(item.endDate),
                                status,
                                gradeText
                            ];
                        } else {
                            return [
                                String(idx + 1),
                                String(item.name || item.title || 'Final Examination'),
                                formatDate(item.endDate),
                                status,
                                hasMarks ? `${Number(m)}%` : '-',
                                gradeText
                            ];
                        }
                    });

                    if (finalTableRows.length === 0) {
                        finalTableRows.push(isStudent
                            ? ["-", "No Final Examination assigned for this module.", "-", "-", "-"]
                            : ["-", "No Final Examination assigned for this module.", "-", "-", "-", "-"]
                        );
                    }

                    runAutoTable(doc, {
                        startY: currentY,
                        head: finalTableHeaders,
                        body: finalTableRows,
                        theme: 'grid',
                        headStyles: {
                            fillColor: [3, 36, 94],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold',
                            fontSize: 8.5,
                            cellPadding: 3,
                        },
                        columnStyles: isStudent
                            ? {
                                0: { cellWidth: 10, halign: 'center' },
                                1: { cellWidth: 90 },
                                2: { cellWidth: 32 },
                                3: { cellWidth: 26, halign: 'center' },
                                4: { cellWidth: 24, halign: 'center' },
                            }
                            : {
                                0: { cellWidth: 10, halign: 'center' },
                                1: { cellWidth: 76 },
                                2: { cellWidth: 30 },
                                3: { cellWidth: 24, halign: 'center' },
                                4: { cellWidth: 20, halign: 'right' },
                                5: { cellWidth: 22, halign: 'center' },
                            },
                        styles: { fontSize: 8, cellPadding: 2.5 },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                const statusColIdx = 3;
                                if (data.column.index === statusColIdx) {
                                    const text = String(data.cell.raw || '');
                                    if (text === 'Graded') {
                                        data.cell.styles.textColor = [22, 163, 74];
                                        data.cell.styles.fontStyle = 'bold';
                                    } else if (text.includes('Ineligible')) {
                                        data.cell.styles.textColor = [220, 38, 38];
                                        data.cell.styles.fontStyle = 'bold';
                                    }
                                }
                            }
                        }
                    });

                    currentY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : currentY + 30) + 6;

                    // Ensure there is room for the Award block (needs ~45mm)
                    if (currentY > 235) {
                        doc.addPage();
                        currentY = 20;
                    }

                    // ==========================================
                    // 5. PART 3: OVERALL COMPOSITE EVALUATION & AWARD
                    // ==========================================
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(3, 36, 94);
                    doc.text("PART 3: FINAL ACADEMIC AWARD & STANDING", margin, currentY);

                    currentY += 4;

                    // Three summary cards
                    const cardGap = 4;
                    const cardWidth = (rightX - margin - (cardGap * 2)) / 3;
                    const cardHeight = 20;

                    // Card 1: CA Average
                    doc.setFillColor(248, 250, 252);
                    doc.setDrawColor(226, 232, 240);
                    doc.roundedRect(margin, currentY, cardWidth, cardHeight, 2, 2, 'FD');
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(100, 116, 139);
                    doc.text("CA AVERAGE (50%)", margin + cardWidth / 2, currentY + 5, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(13);
                    doc.setTextColor(2, 132, 199);
                    doc.text(caEligibility.caAverage !== null && caEligibility.caAverage !== undefined ? `${caEligibility.caAverage}%` : 'Pending', margin + cardWidth / 2, currentY + 12, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text(`${caEligibility.gradedCAs || 0}/${caEligibility.totalCAs || 0} CAs Evaluated`, margin + cardWidth / 2, currentY + 17, { align: 'center', maxWidth: cardWidth - 4 });

                    // Card 2: Final Exam (For student: SHOW ONLY GRADE!)
                    const card2X = margin + cardWidth + cardGap;
                    doc.setFillColor(248, 250, 252);
                    doc.setDrawColor(226, 232, 240);
                    doc.roundedRect(card2X, currentY, cardWidth, cardHeight, 2, 2, 'FD');
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(100, 116, 139);
                    doc.text("FINAL EXAM (50%)", card2X + cardWidth / 2, currentY + 5, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(13);
                    doc.setTextColor(217, 119, 6);

                    let finalCardVal = 'Pending';
                    let finalCardSub = 'Awaiting Exam';

                    if (caEligibility.status === 'NOT_ELIGIBLE') {
                        finalCardVal = 'Grade F';
                        finalCardSub = 'Fail (Ineligible)';
                    } else if (finalPerformance.averageMarks !== null && finalPerformance.averageMarks !== undefined) {
                        if (isStudent) {
                            finalCardVal = `Grade ${getGradeDetails(finalPerformance.averageMarks).grade}`;
                            finalCardSub = getGradeDetails(finalPerformance.averageMarks).classification;
                        } else {
                            finalCardVal = `${finalPerformance.averageMarks}%`;
                            finalCardSub = `Grade ${getGradeDetails(finalPerformance.averageMarks).grade}`;
                        }
                    }
                    doc.text(String(finalCardVal), card2X + cardWidth / 2, currentY + 12, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text(String(finalCardSub), card2X + cardWidth / 2, currentY + 17, { align: 'center', maxWidth: cardWidth - 4 });

                    // Card 3: Overall Standing / Composite Mark
                    const card3X = card2X + cardWidth + cardGap;
                    doc.setFillColor(240, 253, 244);
                    doc.setDrawColor(187, 247, 208);
                    doc.roundedRect(card3X, currentY, cardWidth, cardHeight, 2, 2, 'FD');
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(22, 101, 52);
                    doc.text(isStudent ? "FINAL STANDING" : "COMPOSITE MARK (100%)", card3X + cardWidth / 2, currentY + 5, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(13);
                    doc.setTextColor(21, 128, 61);
                    const compVal = isStudent
                        ? (overallGrade ? `Grade ${overallGrade}` : 'Pending')
                        : (overallMark !== null && overallMark !== undefined ? `${overallMark}%` : 'Pending');
                    doc.text(String(compVal), card3X + cardWidth / 2, currentY + 12, { align: 'center', maxWidth: cardWidth - 4 });
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(22, 101, 52);
                    doc.text(String(overallClassification || 'In Progress'), card3X + cardWidth / 2, currentY + 17, { align: 'center', maxWidth: cardWidth - 4 });

                    currentY += cardHeight + 5;

                    // Award Banner
                    const awardBannerHeight = 22;
                    doc.setFillColor(15, 23, 42); // slate-900
                    doc.roundedRect(margin, currentY, rightX - margin, awardBannerHeight, 2.5, 2.5, 'F');

                    const maxBannerLeftWidth = rightX - margin - 60;
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(148, 163, 184); // slate-400
                    doc.text("FINAL ACADEMIC AWARD & STANDING", margin + 6, currentY + 6, { maxWidth: maxBannerLeftWidth });

                    doc.setFontSize(15);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(56, 189, 248); // sky-400
                    doc.text(`Grade ${overallGrade}`, margin + 6, currentY + 13.5, { maxWidth: maxBannerLeftWidth });

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(226, 232, 240);
                    doc.text(`Classification: ${overallClassification}`, margin + 6, currentY + 18.5, { maxWidth: maxBannerLeftWidth });

                    // Outcome badge (right)
                    let outcomeText = 'IN PROGRESS';
                    let outcomeColor = [245, 158, 11]; // amber

                    if (caEligibility.status === 'NOT_ELIGIBLE') {
                        outcomeText = 'FAILED (INELIGIBLE)';
                        outcomeColor = [239, 68, 68]; // red
                    } else if (isFullyCompleted) {
                        if (overallMark >= 50) {
                            outcomeText = 'PASSED';
                            outcomeColor = [34, 197, 94]; // green
                        } else {
                            outcomeText = 'FAILED';
                            outcomeColor = [239, 68, 68];
                        }
                    }

                    doc.setFillColor(outcomeColor[0], outcomeColor[1], outcomeColor[2]);
                    doc.roundedRect(rightX - 52, currentY + 6, 46, 10, 2, 2, 'F');
                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(255, 255, 255);
                    doc.text(outcomeText, rightX - 29, currentY + 12.5, { align: 'center', maxWidth: 44 });

                    // ==========================================
                    // 6. CERTIFICATION FOOTER (SYSTEM GENERATED)
                    // ==========================================
                    const footerY = 275;
                    doc.setDrawColor(203, 213, 225);
                    doc.setLineWidth(0.5);
                    doc.line(margin, footerY - 5, rightX, footerY - 5);

                    const centerX = margin + (rightX - margin) / 2;

                    doc.setFontSize(8.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(30, 41, 59); // slate-800
                    doc.text("This is an official system generated transcript and does not require any signature.", centerX, footerY, { align: 'center' });

                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 116, 139);
                    doc.text("Grading Scale: Distinction (>= 75%), Merit (>= 65%), Credit (>= 55%), Pass (>= 50%), Fail (< 50%).", centerX, footerY + 4.5, { align: 'center' });

                    doc.setFontSize(7);
                    doc.setTextColor(148, 163, 184);
                    doc.text("Accredited Institution for Higher Professional Education | Web: www.ibas.lk", centerX, footerY + 8.5, { align: 'center' });

                    // Save Document
                    const cleanCode = String(courseCode || 'Course').replace(/[^a-zA-Z0-9_-]/g, '_');
                    const cleanIndex = String(studentIndex || 'Student').replace(/[^a-zA-Z0-9_-]/g, '_');
                    doc.save(`Official_Transcript_${cleanCode}_${cleanIndex}.pdf`);
                    resolve(true);
                } catch (err) {
                    console.error("Error building transcript PDF:", err);
                    reject(err);
                }
            };

            let isBuilt = false;
            const safeBuild = (image) => {
                if (isBuilt) return;
                isBuilt = true;
                buildPdf(image);
            };

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = logoImg;

            if (img.complete && img.naturalWidth !== 0) {
                safeBuild(img);
            } else {
                img.onload = () => safeBuild(img);
                img.onerror = () => safeBuild(null);
                setTimeout(() => safeBuild(null), 1500);
            }
        } catch (error) {
            console.error("Error generating transcript PDF:", error);
            reject(error);
        }
    });
};
